import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cx } from './cx'
import type { Tone } from './tone'

// Charts take colour only from tokens: the main series is the accent (DESIGN.md §2), comparison series are
// neutral ink, status-coloured bars only when the bar *is* a status (wins/draws/losses).
export type SeriesTone = 'accent' | 'ink' | 'muted' | Tone

const strokeTone: Record<SeriesTone, string> = {
  accent: 'stroke-accent',
  ink: 'stroke-ink',
  muted: 'stroke-ink-3',
  positive: 'stroke-positive',
  neutral: 'stroke-neutral',
  warning: 'stroke-warning',
  negative: 'stroke-negative',
  info: 'stroke-info',
}
const fillTone: Record<SeriesTone, string> = {
  accent: 'fill-accent',
  ink: 'fill-ink',
  muted: 'fill-ink-3/35',
  positive: 'fill-positive',
  neutral: 'fill-neutral',
  warning: 'fill-warning',
  negative: 'fill-negative',
  info: 'fill-info',
}
const bgTone: Record<SeriesTone, string> = {
  accent: 'bg-accent',
  ink: 'bg-ink',
  muted: 'bg-ink-3/50',
  positive: 'bg-positive',
  neutral: 'bg-neutral',
  warning: 'bg-warning',
  negative: 'bg-negative',
  info: 'bg-info',
}

const defaultFormat = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 1 })

/** Container width in px, 0 until measured. Charts draw in real pixels so text never stretches. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setWidth(el.clientWidth)
    const ro = new ResizeObserver(([entry]) => entry && setWidth(Math.round(entry.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

/** "Nice" axis ticks (1/2/5 × 10^n) covering [min, max]. */
function ticks(min: number, max: number, count = 4, integer = false): number[] {
  if (min === max) max = min + 1
  const raw = (max - min) / count
  const mag = 10 ** Math.floor(Math.log10(raw))
  // Counts (goals, positions) never get fractional ticks.
  const steps = integer && mag <= 1 ? [1, 2, 5, 10] : [1, 2, 2.5, 5, 10]
  const step = Math.max(integer ? 1 : 0, (steps.find((m) => m * mag >= raw) ?? 10) * mag)
  const out: number[] = []
  for (let v = Math.floor(min / step) * step; v <= max + step * 0.001; v += step) out.push(Number(v.toFixed(10)))
  if ((out.at(-1) ?? 0) < max) out.push((out.at(-1) ?? 0) + step)
  return out
}

/** Ticks for a fixed domain: always both ends (e.g. 1. and 16. place), nice values in between. */
function domainTicks([lo, hi]: [number, number], integer: boolean) {
  const inner = ticks(lo, hi, 3, integer)
  const step = (inner[1] ?? hi) - (inner[0] ?? lo)
  const mid = inner.filter((t) => t - lo > step * 0.4 && hi - t > step * 0.4)
  return [lo, ...mid, hi]
}

type SparklineProps = {
  values: number[]
  /** Accessible summary ("Punkte der letzten 8 Spieltage, steigend"); omit when decorative. */
  label?: string | undefined
  /** Size via utilities ("h-8 w-24"), colour via text-* (stroke is currentColor). */
  className?: string
  area?: boolean
}

export function Sparkline({ values, label, className = 'h-8 w-24 text-accent', area }: SparklineProps) {
  if (values.length < 2) return null
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  // 2 units of padding so the stroke and end dot are not clipped.
  const pts = values.map((v, i) => [(i / (values.length - 1)) * 100, 28 - ((v - min) / span) * 26] as const)
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join('')
  const last = pts.at(-1)!
  return (
    <svg
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cx('overflow-visible', className)}
    >
      {area && <path d={`${d}L100,30L0,30Z`} className="fill-current opacity-10" />}
      <path d={d} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {/* Non-scaling end dot: a zero-length round-capped line stays circular under preserveAspectRatio=none. */}
      <path d={`M${last[0]},${last[1]}h0`} stroke="currentColor" strokeWidth={4} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

type ChartFrameProps = { title: ReactNode; description?: ReactNode; children: ReactNode; table: ReactNode; legend?: ReactNode }

function ChartFrame({ title, description, children, table, legend }: ChartFrameProps) {
  return (
    <figure className="flex min-w-0 flex-col gap-3">
      <figcaption className="flex flex-col gap-0.5">
        <span className="text-small font-medium text-ink">{title}</span>
        {description && <span className="text-small text-ink-3">{description}</span>}
      </figcaption>
      {legend}
      {children}
      {/* The SVG is aria-hidden; this table is what screen readers read. */}
      <div className="sr-only">{table}</div>
    </figure>
  )
}

const axisText = 'fill-ink-3 text-label tracking-normal tabular'

export type BarDatum = { label: string; value: number; tone?: SeriesTone; detail?: string }

type BarChartProps = {
  title: ReactNode
  description?: ReactNode
  data: BarDatum[]
  height?: number
  format?: (n: number) => string
  /** Print values above bars (few bars only). */
  showValues?: boolean
  valueLabel?: string
}

export function BarChart({ title, description, data, height = 200, format = defaultFormat, showValues, valueLabel = 'Wert' }: BarChartProps) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const values = data.map((d) => d.value)
  const yt = ticks(Math.min(0, ...values), Math.max(0, ...values), 4, values.every(Number.isInteger))
  const lo = yt[0] ?? 0
  const hi = yt.at(-1) ?? 1
  const left = 36
  const top = showValues ? 18 : 8
  const bottom = 24
  const plotW = Math.max(0, width - left)
  const plotH = height - top - bottom
  const y = (v: number) => top + plotH - ((v - lo) / (hi - lo)) * plotH
  const band = data.length ? plotW / data.length : 0
  const barW = Math.min(36, band * 0.62)
  const labelEvery = Math.max(1, Math.ceil(data.length / Math.max(1, Math.floor(plotW / 44))))

  return (
    <ChartFrame
      title={title}
      description={description}
      table={
        <table>
          <thead>
            <tr>
              <th>Kategorie</th>
              <th>{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.label}>
                <td>{d.label}</td>
                <td>{format(d.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <div ref={ref} className="w-full" style={{ height }}>
        {width > 0 && (
          <svg width={width} height={height} aria-hidden="true" className="block overflow-visible">
            {yt.map((t) => (
              <g key={t}>
                <line x1={left} x2={width} y1={y(t)} y2={y(t)} className={t === 0 ? 'stroke-line-strong' : 'stroke-line'} />
                <text x={left - 8} y={y(t)} dy="0.32em" textAnchor="end" className={axisText}>
                  {format(t)}
                </text>
              </g>
            ))}
            {data.map((d, i) => {
              const cx0 = left + band * i + band / 2
              const y0 = y(Math.max(0, d.value))
              const h = Math.abs(y(d.value) - y(0))
              const r = Math.min(3, h / 2, barW / 2)
              const x = cx0 - barW / 2
              // Rounded on the value end only; flat on the baseline.
              const path =
                d.value >= 0
                  ? `M${x},${y0 + h}V${y0 + r}q0,-${r} ${r},-${r}H${x + barW - r}q${r},0 ${r},${r}V${y0 + h}Z`
                  : `M${x},${y0}V${y0 + h - r}q0,${r} ${r},${r}H${x + barW - r}q${r},0 ${r},-${r}V${y0}Z`
              return (
                <g key={d.label}>
                  <path d={path} className={cx(fillTone[d.tone ?? 'accent'], 'transition-opacity hover:opacity-80')}>
                    <title>{`${d.label}: ${format(d.value)}${d.detail ? ` · ${d.detail}` : ''}`}</title>
                  </path>
                  {showValues && (
                    <text x={cx0} y={y(Math.max(0, d.value)) - 6} textAnchor="middle" className="fill-ink-2 text-label font-medium tracking-normal tabular">
                      {format(d.value)}
                    </text>
                  )}
                  {i % labelEvery === 0 && (
                    <text x={cx0} y={height - 6} textAnchor="middle" className={axisText}>
                      {d.label}
                    </text>
                  )}
                </g>
              )
            })}
          </svg>
        )}
      </div>
    </ChartFrame>
  )
}

export type LineSeries = { label: string; values: (number | null)[]; tone?: SeriesTone }

type LineChartProps = {
  title: ReactNode
  description?: ReactNode
  series: LineSeries[]
  xLabels: string[]
  height?: number
  format?: (n: number) => string
  /** Fix the y-range, e.g. [0, 1] for quotas or [1, 16] for table positions. */
  yDomain?: [number, number]
  /** Larger values drawn lower (table position: 1 at the top). */
  invert?: boolean
}

export function LineChart({ title, description, series, xLabels, height = 220, format = defaultFormat, yDomain, invert }: LineChartProps) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null))
  const integer = all.every(Number.isInteger)
  const yt = yDomain ? domainTicks(yDomain, integer) : ticks(Math.min(...all), Math.max(...all), 4, integer)
  const lo = yDomain?.[0] ?? yt[0] ?? 0
  const hi = yDomain?.[1] ?? yt.at(-1) ?? 1
  const left = 36
  const right = 8
  const top = 8
  const bottom = 24
  const plotW = Math.max(0, width - left - right)
  const plotH = height - top - bottom
  const n = xLabels.length
  const x = (i: number) => left + (n > 1 ? (i / (n - 1)) * plotW : plotW / 2)
  const y = (v: number) => {
    const t = (v - lo) / (hi - lo || 1)
    return top + (invert ? t : 1 - t) * plotH
  }
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 48))))

  const legend =
    series.length > 1 ? (
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-small text-ink-2" aria-hidden="true">
        {series.map((s) => (
          <li key={s.label} className="inline-flex items-center gap-2">
            <span className={cx('h-0.5 w-3.5 rounded-full', bgTone[s.tone ?? 'accent'])} />
            {s.label}
          </li>
        ))}
      </ul>
    ) : undefined

  return (
    <ChartFrame
      title={title}
      description={description}
      legend={legend}
      table={
        <table>
          <thead>
            <tr>
              <th>Punkt</th>
              {series.map((s) => (
                <th key={s.label}>{s.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {xLabels.map((l, i) => (
              <tr key={i}>
                <td>{l}</td>
                {series.map((s) => {
                  const v = s.values[i]
                  return <td key={s.label}>{v === null || v === undefined ? '–' : format(v)}</td>
                })}
              </tr>
            ))}
          </tbody>
        </table>
      }
    >
      <div ref={ref} className="w-full" style={{ height }}>
        {width > 0 && (
          <svg width={width} height={height} aria-hidden="true" className="block overflow-visible">
            {yt.map((t) => (
              <g key={t}>
                <line x1={left} x2={width - right} y1={y(t)} y2={y(t)} className="stroke-line" />
                <text x={left - 8} y={y(t)} dy="0.32em" textAnchor="end" className={axisText}>
                  {format(t)}
                </text>
              </g>
            ))}
            {xLabels.map((l, i) =>
              i % labelEvery === 0 || i === n - 1 ? (
                <text key={i} x={x(i)} y={height - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} className={axisText}>
                  {l}
                </text>
              ) : null,
            )}
            {/* Comparison series first so the main (first) series draws on top. */}
            {series.toReversed().map((s, si) => {
              const main = si === series.length - 1
              const tone = s.tone ?? (main ? 'accent' : 'muted')
              // Gaps (null) split the line instead of dropping to zero.
              let d = ''
              let pen = false
              s.values.forEach((v, i) => {
                if (v === null) return void (pen = false)
                d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`
                pen = true
              })
              return (
                <g key={s.label}>
                  <path d={d} fill="none" strokeWidth={main ? 2 : 1.5} strokeLinejoin="round" strokeLinecap="round" className={strokeTone[tone]} strokeDasharray={main ? undefined : '4 3'} />
                  {s.values.map((v, i) =>
                    v === null ? null : (
                      <circle
                        key={i}
                        cx={x(i)}
                        cy={y(v)}
                        r={main ? 3 : 2.5}
                        strokeWidth={2}
                        className={cx(strokeTone[tone], 'fill-surface')}
                      >
                        <title>{`${xLabels[i] ?? ''} · ${s.label}: ${format(v)}`}</title>
                      </circle>
                    ),
                  )}
                </g>
              )
            })}
          </svg>
        )}
      </div>
    </ChartFrame>
  )
}
